import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Table, Btn, Input, Select, Badge, Modal, SectionCard, SectionLabel, sectionLabelStyle, Stat } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import CustomerWorkspace from './CustomerWorkspace';
import FindClients from './FindClients';
import { ClientsRegistry } from './ClientsTool';
import { StatusChip, FlagChips } from './WayOfSelling';

// Reserved tab keys — anything else in /customers/:sub is treated as a customer id (the workspace).
const TABS = ['journeys', 'funnel', 'week', 'clients', 'find'];

const CUSTOMERS_SUBNAV = [
  { href: '/customers/journeys', label: 'Journeys' },
  { href: '/customers/funnel', label: 'Funnel' },
  { href: '/customers/week', label: 'My week' },
  { href: '/customers/clients', label: 'HB Clients' },
  { href: '/customers/find', label: 'Find clients' },
];

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// ── Project Modal ──────────────────────────────────────────────────────────
function ProjectModal({ project, onClose, onChanged }) {
  const [nextStep, setNextStep] = useState(project.nextStep || '');
  const [deal, setDeal] = useState({
    value: project.value ?? '', currency: project.currency || 'EUR',
    probability: project.probability ?? '', expectedCloseDate: project.expectedCloseDate ? String(project.expectedCloseDate).slice(0, 10) : '',
  });
  const [party, setParty] = useState({ role: 'sponsor', name: '' });
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const user = getUser();
  const isOwner = project.ownerId === user?.id;

  useEffect(() => { api.getMembers().then(d => setMembers(d.members || [])).catch(() => {}); }, []);

  async function save() {
    setBusy(true);
    try {
      await api.explore.updateProject(project.id, {
        nextStep,
        value: deal.value === '' ? null : Number(deal.value),
        currency: deal.currency,
        probability: deal.probability === '' ? null : Number(deal.probability),
        expectedCloseDate: deal.expectedCloseDate || null,
      });
      onChanged(); onClose();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function addCoSeller(userId) {
    try { await api.explore.addCoSeller(project.id, Number(userId)); onChanged(); } catch (e) { alert(e.message); }
  }
  async function removeCoSeller(userId) {
    try { await api.explore.removeCoSeller(project.id, userId); onChanged(); } catch (e) { alert(e.message); }
  }
  async function handover(newOwnerId) {
    if (!newOwnerId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Hand over ownership of this project? History is preserved.')) return;
    try { await api.explore.handover(project.id, Number(newOwnerId)); onChanged(); onClose(); } catch (e) { alert(e.message); }
  }
  const coSellerIds = new Set((project.coSellers || []).map(c => c.id));
  async function closeWon() {
    setBusy(true);
    try { await api.explore.updateProject(project.id, { closedWon: true }); onChanged(); onClose(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function addParty(e) {
    e.preventDefault();
    if (!party.name.trim()) return;
    try { await api.explore.addParty(project.id, party); setParty({ role: 'sponsor', name: '' }); onChanged(); }
    catch (err) { alert(err.message); }
  }

  return (
    <Modal open onClose={onClose} title={project.name} width={660} footer={<>
      {!project.closedWonDate && !project.wos?.stepBased && (
        <Btn variant="outline" size="sm" loading={busy} onClick={closeWon}>Agreement reached — close as won</Btn>
      )}
      <Btn size="sm" loading={busy} onClick={save} style={{ marginLeft: 'auto' }}>Save</Btn>
    </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ fontSize: '0.88rem', color: 'var(--ink-soft)' }}>
          Customer: <b>{project.customer}</b>{project.contact ? ` · ${project.contact}` : ''}
        </div>

        <StageRail projectId={project.id} onChanged={onChanged} />

        <div style={groupStyle}>
          <SectionLabel>Next step</SectionLabel>
          <Input value={nextStep} onChange={e => setNextStep(e.target.value)} placeholder="Next step" />
        </div>

        {/* Deal value & forecast (WI-B6). HB Way of Selling v1.1: value and chance live in the workspace. */}
        {project.wos?.stepBased ? (
          <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>
            Value, chance and the steps of HB Way of Selling v1.1 are kept in the customer workspace — <b>Open</b> the project.
          </div>
        ) : <div style={groupStyle}>
          <SectionLabel>Deal</SectionLabel>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Input type="number" min="0" value={deal.value} onChange={e => setDeal(d => ({ ...d, value: e.target.value }))} placeholder="Est. value" style={{ maxWidth: 130 }} />
            <Select value={deal.currency} onChange={e => setDeal(d => ({ ...d, currency: e.target.value }))} style={{ maxWidth: 90 }}>
              <option>EUR</option><option>USD</option><option>RSD</option><option>GBP</option>
            </Select>
            <Input type="number" min="0" max="100" value={deal.probability} onChange={e => setDeal(d => ({ ...d, probability: e.target.value }))} placeholder="Prob %" style={{ maxWidth: 100 }} />
            <Input type="date" value={deal.expectedCloseDate} onChange={e => setDeal(d => ({ ...d, expectedCloseDate: e.target.value }))} style={{ maxWidth: 170 }} />
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)', marginTop: 4 }}>
            Probability defaults per stage and updates as the gates move the project — edit if you know better.
          </div>
        </div>}

        {/* Ownership & collaboration (WI-B5) */}
        <div style={groupStyle}>
          <SectionLabel>Ownership</SectionLabel>
          <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>Owner: <b>{project.owner || '—'}</b></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {(project.coSellers || []).map(c => (
              <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', background: 'var(--canvas-warm)', borderRadius: 99, padding: '2px 6px 2px 10px' }}>
                {c.name}
                {isOwner && <button onClick={() => removeCoSeller(c.id)} style={{ ...linkBtn, textDecoration: 'none', color: 'var(--ink-faint)' }}>×</button>}
              </span>
            ))}
            {!(project.coSellers || []).length && <span style={{ fontSize: '0.8rem', color: 'var(--ink-faint)' }}>No co-sellers</span>}
          </div>
          {isOwner && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Select value="" onChange={e => e.target.value && addCoSeller(e.target.value)} style={{ maxWidth: 200 }}>
                <option value="">Add co-seller…</option>
                {members.filter(m => m.id !== project.ownerId && !coSellerIds.has(m.id)).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
              <Select value="" onChange={e => handover(e.target.value)} style={{ maxWidth: 200 }}>
                <option value="">Hand over to…</option>
                {members.filter(m => m.id !== project.ownerId).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
            </div>
          )}
        </div>

        <div style={groupStyle}>
          <SectionLabel>Parties</SectionLabel>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.9 }}>
            {project.parties.map(p => <div key={p.id}>▪ <b style={{ textTransform: 'capitalize' }}>{p.role}</b>: {p.name}{p.contact ? ` (${p.contact})` : ''}</div>)}
            {!project.parties.length && <span style={{ color: 'var(--ink-faint)' }}>No parties yet</span>}
          </div>
          <form onSubmit={addParty} style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Select value={party.role} onChange={e => setParty(p => ({ ...p, role: e.target.value }))} style={{ maxWidth: 170 }}>
              <option value="sponsor">sponsor</option>
              <option value="partner">partner</option>
              <option value="participant group">participant group</option>
              <option value="mentor">mentor</option>
            </Select>
            <Input value={party.name} onChange={e => setParty(p => ({ ...p, name: e.target.value }))} placeholder="Name" style={{ flex: 1, minWidth: 140 }} />
            <Btn type="submit" size="sm" variant="outline">Add</Btn>
          </form>
        </div>
        <div style={groupStyle}><FitChecklist projectId={project.id} onChanged={onChanged} /></div>

        <ProjectExtras projectId={project.id} />
      </div>
    </Modal>
  );
}

// ── Stage rail (WI-B2) ─────────────────────────────────────────────────────
function StageRail({ projectId, onChanged }) {
  const navigate = useNavigate();
  const [rail, setRail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.explore.getRail(projectId).then(setRail).catch(() => setRail({ stages: [] }));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!rail) return <div style={{ padding: 10 }}><Spinner size={16} /></div>;
  if (!rail.stages || !rail.stages.length) return null; // project not on a process

  const current = rail.stages.find(s => s.id === rail.currentStageId) || rail.stages[0];
  const changed = () => { load(); onChanged && onChanged(); };

  async function toggle(m) {
    if (m.kind === 'auto') return;
    try { await api.explore.toggleMilestone(projectId, m.id); changed(); } catch (e) { alert(e.message); }
  }
  async function delivered() {
    setBusy(true);
    try { await api.explore.markDelivered(projectId, !!rail.deliveredAt); changed(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function lost() {
    try {
      if (rail.isLost) { await api.explore.markLost(projectId, { reopen: true }); changed(); return; }
      const list = rail.lostReasons.map((r, i) => `${i + 1}. ${r.label}`).join('\n');
      // eslint-disable-next-line no-alert
      const pick = window.prompt(`Mark project lost — choose a reason:\n${list}\n\nEnter number:`);
      const idx = parseInt(pick, 10);
      if (idx >= 1 && idx <= rail.lostReasons.length) { await api.explore.markLost(projectId, { reasonId: rail.lostReasons[idx - 1].id }); changed(); }
    } catch (e) { alert(e.message); }
  }

  // What moves this project on from here — the stage is never moved by hand.
  const NEXT_EVENT = {
    1: 'Open a role track in the customer workspace and start discovering the need.',
    2: 'Confirm the need with the customer — Gate 1.',
    3: 'Confirm the design (programme, with or without an HB Compass profile) — Gate 2.',
    0: 'Hold the first meeting, then confirm “1 — Prospect” in the customer workspace.',
    4: 'Send the proposal; when the decision maker accepts it in principle — Gate 3.',
    5: 'Agreement signed — Gate 4 (or record the outcome: lost with the reason, parked, unresponsive).',
    6: 'Deliver; when the re-assessment results are presented to the sponsor — Gate 5.',
    7: 'Completed — back in Explore for the customer’s next need.',
  };

  return (
    <div style={{ background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
      {rail.isLost && <Alert type="warn">This project is marked <b>lost</b>. <button onClick={lost} style={linkBtn}>Reopen</button></Alert>}

      {/* Stage chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {rail.stages.map(s => {
          const cur = s.id === current.id;
          return (
            <span key={s.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 99,
              fontSize: '0.76rem', fontWeight: cur ? 700 : 400,
              background: cur ? 'var(--ink)' : 'transparent', color: cur ? '#fff' : 'var(--ink-soft)',
              border: cur ? '1px solid var(--ink)' : '1px solid rgba(0,0,0,0.12)',
            }}>
              {s.anchor && <b style={{ opacity: 0.7 }}>{s.anchor}</b>}{s.seq}. {s.name}
            </span>
          );
        })}
      </div>

      {/* Current stage detail */}
      <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 8 }}>
        {current.entryCriterion && <div>Here because: {current.entryCriterion}</div>}
        {NEXT_EVENT[current.seq] && <div style={{ marginTop: 3 }}><b>Moves on when:</b> {NEXT_EVENT[current.seq]}</div>}
      </div>

      <div style={{ marginBottom: 10 }}>
        {current.milestones.map(m => (
          <label key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.85rem', padding: '3px 0', cursor: m.kind === 'auto' ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={m.done} disabled={m.kind === 'auto'} onChange={() => toggle(m)} style={{ marginTop: 3 }} />
            <span style={{ color: m.done ? 'var(--ink-faint)' : 'var(--ink)', textDecoration: m.done ? 'line-through' : 'none' }}>
              {m.label} <span style={{ color: 'var(--ink-faint)', fontSize: '0.72rem' }}>{m.kind === 'auto' ? '◆ auto' : '● manual'}</span>
            </span>
          </label>
        ))}
      </div>

      {/* Stage kit — references only */}
      {current.kit.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 4 }}>What you need at this stage</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {current.kit.map(k => (
              <button key={k.id} onClick={() => k.route && navigate(k.route)} style={{ ...linkBtn, fontSize: '0.78rem', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, padding: '3px 8px', background: 'var(--canvas-white)' }}>
                {k.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {current.primaryActionRoute && (
          <Btn size="sm" onClick={() => navigate(`${current.primaryActionRoute}?project=${projectId}`)}>
            {current.primaryActionLabel || 'Continue'} →
          </Btn>
        )}
        {rail.wonAt && !rail.isLost && (current.seq === 6 || rail.deliveredAt) && (
          <Btn size="sm" variant="outline" loading={busy} onClick={delivered}>
            {rail.deliveredAt ? 'Undo “programme delivered”' : 'Programme delivered'}
          </Btn>
        )}
        {!rail.isLost && <button onClick={lost} style={{ ...linkBtn, color: 'var(--danger)', fontSize: '0.8rem', marginLeft: 'auto' }}>Mark lost</button>}
      </div>

      {rail.history.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: '0.76rem', color: 'var(--ink-faint)', cursor: 'pointer' }}>Stage history ({rail.history.length})</summary>
          <div style={{ marginTop: 6 }}>
            {rail.history.map((h, i) => (
              <div key={i} style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>
                → {h.stage}{h.by ? ` · ${h.by}` : ''}{h.overrideReason ? ` · ${h.overrideReason}` : ''}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink-soft)', fontFamily: 'var(--font-body)', textDecoration: 'underline' };
const sectionLabel = sectionLabelStyle;
// One group of the project card — separated by a hairline so the sections read as blocks.
const groupStyle = { borderTop: '1px solid var(--line)', paddingTop: 14 };

// Documents (SharePoint links) + activities (with due dates) for a project (WI-B7).
function ProjectExtras({ projectId }) {
  const [docs, setDocs] = useState([]);
  const [acts, setActs] = useState([]);
  const [doc, setDoc] = useState({ label: '', url: '', docType: 'proposal' });
  const [act, setAct] = useState({ title: '', dueDate: '' });

  const load = useCallback(() => {
    api.explore.getDocuments(projectId).then(d => setDocs(d.documents)).catch(() => {});
    api.explore.getActivities(projectId).then(d => setActs(d.activities)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  async function addDoc(e) {
    e.preventDefault();
    if (!doc.label.trim() || !doc.url.trim()) return;
    try { await api.explore.addDocument(projectId, doc); setDoc({ label: '', url: '', docType: 'proposal' }); load(); } catch (err) { alert(err.message); }
  }
  async function addAct(e) {
    e.preventDefault();
    if (!act.title.trim()) return;
    try { await api.explore.addActivity(projectId, { title: act.title, dueDate: act.dueDate || null }); setAct({ title: '', dueDate: '' }); load(); } catch (err) { alert(err.message); }
  }
  const overdue = (d) => d && new Date(d) < new Date(new Date().toDateString());

  return (
    <>
      <div style={groupStyle}>
        <div style={sectionLabel}>Documents</div>
        <div style={{ fontSize: '0.85rem', lineHeight: 1.9 }}>
          {docs.map(d => (
            <div key={d.id}>
              <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'var(--ink)' }}>{d.label}</a>
              <span style={{ color: 'var(--ink-faint)', fontSize: '0.74rem' }}> · {d.type}</span>{' '}
              <button onClick={() => api.explore.deleteDocument(projectId, d.id).then(load)} style={{ ...linkBtn, textDecoration: 'none', color: 'var(--ink-faint)' }}>×</button>
            </div>
          ))}
          {!docs.length && <span style={{ color: 'var(--ink-faint)' }}>No documents linked</span>}
        </div>
        <form onSubmit={addDoc} style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <Input value={doc.label} onChange={e => setDoc(s => ({ ...s, label: e.target.value }))} placeholder="Label" style={{ flex: 1, minWidth: 120 }} />
          <Input value={doc.url} onChange={e => setDoc(s => ({ ...s, url: e.target.value }))} placeholder="SharePoint / URL" style={{ flex: 2, minWidth: 160 }} />
          <Select value={doc.docType} onChange={e => setDoc(s => ({ ...s, docType: e.target.value }))} style={{ maxWidth: 130 }}>
            <option value="intake">intake</option><option value="proposal">proposal</option><option value="agreement">agreement</option><option value="other">other</option>
          </Select>
          <Btn type="submit" size="sm" variant="outline">Link</Btn>
        </form>
      </div>

      <div style={groupStyle}>
        <div style={sectionLabel}>Activities &amp; due dates</div>
        <div style={{ fontSize: '0.85rem' }}>
          {acts.map(a => (
            <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
              <input type="checkbox" checked={a.done} onChange={() => api.explore.toggleActivity(projectId, a.id).then(load)} />
              <span style={{ textDecoration: a.done ? 'line-through' : 'none', color: a.done ? 'var(--ink-faint)' : 'var(--ink)' }}>{a.title}</span>
              {a.dueDate && <span style={{ fontSize: '0.74rem', color: overdue(a.dueDate) && !a.done ? 'var(--danger)' : 'var(--ink-faint)' }}>· due {String(a.dueDate).slice(0, 10)}</span>}
              <button onClick={() => api.explore.deleteActivity(projectId, a.id).then(load)} style={{ ...linkBtn, textDecoration: 'none', color: 'var(--ink-faint)', marginLeft: 'auto' }}>×</button>
            </label>
          ))}
          {!acts.length && <span style={{ color: 'var(--ink-faint)' }}>No activities yet</span>}
        </div>
        <form onSubmit={addAct} style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <Input value={act.title} onChange={e => setAct(s => ({ ...s, title: e.target.value }))} placeholder="Activity / next step" style={{ flex: 2, minWidth: 160 }} />
          <Input type="date" value={act.dueDate} onChange={e => setAct(s => ({ ...s, dueDate: e.target.value }))} style={{ maxWidth: 160 }} />
          <Btn type="submit" size="sm" variant="outline">Add</Btn>
        </form>
      </div>
    </>
  );
}

// ── My Projects (CRM) ─────────────────────────────────────────────────────
// ── UC1: my criteria — "who is worth it", written down in advance ─────────
const WEIGHTS = [[3, 'must'], [2, 'important'], [1, 'nice']];

function CriteriaEditor({ onSaved }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.explore.getCriteria().then(d => setList(d.criteria || [])).catch(() => setList([])); }, []);
  if (list === null) return null;
  const set = (i, k, v) => setList(l => l.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  async function save() {
    setBusy(true);
    try { await api.explore.saveCriteria(list.filter(c => c.label.trim())); const d = await api.explore.getCriteria(); setList(d.criteria || []); setOpen(false); onSaved(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <Card style={{ padding: '12px 18px', margin: '14px 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <b style={{ fontSize: '0.9rem' }}>My criteria — who is worth my time</b>
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>
            {list.length ? list.map(c => `${c.label}${c.weight === 3 ? ' (must)' : ''}`).join(' · ') : 'Write down the kind of company and project you want — your pipeline is scored against your criteria, not a company average.'}
          </div>
        </div>
        <Btn size="sm" variant={open ? 'ghost' : 'outline'} onClick={() => setOpen(o => !o)}>{open ? 'Close' : list.length ? 'Edit' : 'Write my criteria'}</Btn>
      </div>
      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((c, i) => (
            <div key={c.id || `n${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input value={c.label} onChange={e => set(i, 'label', e.target.value)} placeholder="e.g. 200+ employees · sales-driven · sponsor at board level" maxLength={200} />
              <Select value={c.weight} onChange={e => set(i, 'weight', Number(e.target.value))} style={{ width: 130 }}>
                {WEIGHTS.map(([w, l]) => <option key={w} value={w}>{l}</option>)}
              </Select>
              <button onClick={() => setList(l => l.filter((_, j) => j !== i))} aria-label="Remove" style={{ ...linkBtn, textDecoration: 'none', fontSize: '1.1rem' }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant="ghost" onClick={() => setList(l => [...l, { label: '', weight: 2 }])} disabled={list.length >= 20}>+ criterion</Btn>
            <Btn size="sm" loading={busy} onClick={save}>Save criteria</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}

function FitChecklist({ projectId, onChanged }) {
  const [fit, setFit] = useState(null);
  const load = useCallback(() => { api.explore.getFit(projectId).then(setFit).catch(() => setFit({ criteria: [] })); }, [projectId]);
  useEffect(() => { load(); }, [load]);
  if (!fit) return null;
  if (!fit.criteria.length) return <div style={{ fontSize: '0.8rem', color: 'var(--ink-faint)' }}>Write your criteria in My Pipeline to score how well this project fits.</div>;
  async function toggle(c) { try { await api.explore.toggleFit(projectId, c.id); load(); onChanged && onChanged(); } catch (e) { alert(e.message); } }
  return (
    <div>
      <div style={{ ...sectionLabel }}>Fit with my criteria{fit.score != null ? ` · ${fit.score}%` : ''}</div>
      {fit.criteria.map(c => (
        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', padding: '2px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={c.met} onChange={() => toggle(c)} />
          <span>{c.label}{c.weight === 3 && <span style={{ color: 'var(--ink-faint)', fontSize: '0.72rem' }}> · must</span>}</span>
        </label>
      ))}
      {fit.mustMissing.length > 0 && <div style={{ fontSize: '0.76rem', color: 'var(--danger)', marginTop: 4 }}>Missing a must: {fit.mustMissing.join(', ')} — is this worth your time?</div>}
    </div>
  );
}

function Projects() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, projects: [] });
  const [form, setForm] = useState({ name: '', customer: '', companyId: null, contact: '', sourceType: '' });
  const [openId, setOpenId] = useState(null);
  const [openCompanyId, setOpenCompanyId] = useState(null);
  const [view, setViewState] = useState(() => { try { return localStorage.getItem('bc-journeys-view') || 'board'; } catch { return 'board'; } });
  const [scope, setScope] = useState('active');
  const [stages, setStages] = useState([]);
  const user = getUser();
  const setView = (v) => { setViewState(v); try { localStorage.setItem('bc-journeys-view', v); } catch { /* per-viewer only */ } };

  useEffect(() => {
    api.getProcess().then(d => setStages((d.process?.stages || []).map(x => ({ seq: x.seq, name: x.name })))).catch(() => setStages([]));
  }, []);

  const load = useCallback(() => {
    api.explore.getProjects()
      .then(d => setState({ loading: false, error: null, projects: d.projects }))
      .catch(e => setState({ loading: false, error: e.message, projects: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function add(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.customer.trim()) return;
    try {
      await api.explore.createProject({ name: form.name, companyName: form.customer, companyId: form.companyId, contact: form.contact, sourceType: form.sourceType || null });
      setForm({ name: '', customer: '', companyId: null, contact: '', sourceType: '' }); load();
    } catch (err) { alert(err.message); }
  }
  async function decide(id, decision) {
    try { await api.explore.decideGraduation(id, decision); load(); }
    catch (e) { alert(e.message); }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  const open = state.projects.find(p => p.id === openId);
  const proposals = state.projects.filter(p => p.graduationStatus === 'proposed' && p.ownerId === user?.id);

  // Visual contract (Pipeline): key numbers, then the projects as a board by stage or a list.
  const statusOf = (p) => p.wos?.status || (p.isLost ? 'lost' : p.deliveredAt ? 'completed' : p.closedWonDate ? 'won — delivering' : 'open');
  const isActive = (p) => ['open', 'won — delivering'].includes(statusOf(p));
  const openOnes = state.projects.filter(p => statusOf(p) === 'open');
  const sum = (xs, f) => xs.reduce((t, p) => t + (f(p) || 0), 0);
  const openValue = sum(openOnes, p => p.value);
  const weighted = sum(openOnes, p => (p.value != null && p.probability != null ? Math.round(p.value * p.probability / 100) : 0));
  const count = (f) => state.projects.filter(f).length;
  const kpis = [
    [openOnes.length, 'open'],
    [`${openValue.toLocaleString('en-GB')} €`, 'open value'],
    [`${weighted.toLocaleString('en-GB')} €`, 'weighted'],
    [count(p => p.wos?.flags?.overdue), 'next steps overdue', 'var(--danger)'],
    [count(p => p.wos?.flags?.stale), 'no change 30+ days', 'var(--warn)'],
    [count(p => p.wos?.flags?.k), 'Gate 1 without checkpoint', '#075985'],
    [count(p => ['won — delivering', 'completed'].includes(statusOf(p))), 'won'],
  ];
  const shown = scope === 'active' ? state.projects.filter(isActive) : state.projects;
  const seqOf = (p) => p.wos?.stageSeq ?? (stages.find(x => x.name === p.stage)?.seq ?? 0);
  const columns = stages.length ? stages : [...new Set(shown.map(seqOf))].sort((a, b) => a - b).map(seq => ({ seq, name: shown.find(p => seqOf(p) === seq)?.stage || '' }));

  const list = (
    <Card style={{ padding: '10px 24px 6px', marginBottom: 14 }}>
      <Table
        headers={['Project', 'Customer', 'Fit', 'Parties', 'Stage', 'Next step', '']}
        rows={shown.map(p => [
          <button onClick={() => navigate(`/customers/${p.id}`)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--ink)', fontFamily: 'var(--font-body)', fontSize: '0.88rem', textAlign: 'left' }}>{p.name}</button>,
          p.companyId
            ? <button onClick={() => setOpenCompanyId(p.companyId)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontFamily: 'var(--font-body)', fontSize: '0.88rem', textAlign: 'left' }}>{p.customer}</button>
            : <span>{p.customer || '—'}</span>,
          p.fit != null
            ? <span title="How well this project fits your own criteria" style={{ fontWeight: 600, color: p.fit >= 70 ? 'var(--ink)' : p.fit >= 40 ? 'var(--ink-soft)' : 'var(--danger)' }}>{p.fit}%</span>
            : <span style={{ color: 'var(--ink-faint)' }}>—</span>,
          <span style={{ fontSize: '0.8rem' }}>{p.parties.map(x => `${x.role}: ${x.name}`).join(' · ') || '—'}</span>,
          <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
            {p.stage}
            {p.graduationStatus === 'approved' && <Badge status="completed"> in registry</Badge>}
            {p.wos && p.wos.status !== 'open' && <StatusChip status={p.wos.status} />}
            {p.wos && <FlagChips flags={p.wos.flags} />}
          </span>,
          p.nextStep || '—',
          <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Btn size="sm" variant="outline" onClick={() => navigate(`/customers/${p.id}`)}>Open</Btn>
            <Btn variant="link" onClick={() => setOpenId(p.id)} style={{ fontSize: '0.82rem', color: 'var(--ink-soft)' }}>Edit</Btn>
          </span>,
        ])}
        emptyMessage="No projects yet — add your first prospect below"
      />
    </Card>
  );

  const board = (
    <Card style={{ padding: 14, marginBottom: 14 }}>
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(112px, 1fr))`, gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
      {columns.map(col => {
        const cards = shown.filter(p => seqOf(p) === col.seq);
        return (
          <div key={col.seq} style={{ background: '#f9fafb', border: 'var(--panel-border)', borderRadius: 12, padding: 8, minHeight: 140 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, padding: '4px 4px 8px', lineHeight: 1.3 }}>
              {col.seq} — {col.name} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({cards.length})</span>
            </div>
            {cards.map(p => (
              <div key={p.id} role="button" tabIndex={0}
                onClick={() => navigate(`/customers/${p.id}`)}
                onKeyDown={e => { if (e.key === 'Enter') navigate(`/customers/${p.id}`); }}
                style={{ background: 'var(--canvas-white)', borderRadius: 10, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', fontSize: '0.86rem', border: 'var(--panel-border)', boxShadow: '0 1px 2px rgba(0,0,0,.05)' }}>
                <div style={{ fontWeight: 700, lineHeight: 1.3 }}>{p.name}</div>
                <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 2 }}>{p.customer || '—'}</div>
                {p.value != null && <div style={{ fontSize: '0.8rem', marginTop: 4 }}>{p.value.toLocaleString('en-GB')} €{p.probability != null ? ` · ${p.probability}%` : ''}</div>}
                {(p.wos && (p.wos.status !== 'open' || Object.values(p.wos.flags || {}).some(Boolean))) && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                    {p.wos.status !== 'open' && <StatusChip status={p.wos.status} />}
                    <FlagChips flags={p.wos.flags} />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
    </Card>
  );

  return (
    <>
      {proposals.map(p => (
        <div key={p.id} style={{ marginBottom: 10 }}>
          <Alert type="warn">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, minWidth: 240 }}><b>{p.customer}</b> became a client through “{p.name}” — a proposal to enter it in the shared HB Clients registry awaits your decision.</span>
              <span style={{ display: 'inline-flex', gap: 8 }}>
                <Btn size="sm" variant="outline" onClick={() => decide(p.id, 'decline')}>Decline</Btn>
                <Btn size="sm" onClick={() => decide(p.id, 'approve')}>Approve</Btn>
              </span>
            </div>
          </Alert>
        </div>
      ))}

      <SectionCard title="Journeys" action={<>
        <Select value={scope} onChange={e => setScope(e.target.value)} style={{ width: 'auto' }}>
          <option value="active">Active (open + delivering)</option>
          <option value="all">All projects</option>
        </Select>
        <Btn size="sm" variant={view === 'board' ? 'primary' : 'outline'} onClick={() => setView('board')}>Board by stage</Btn>
        <Btn size="sm" variant={view === 'list' ? 'primary' : 'outline'} onClick={() => setView('list')}>List</Btn>
      </>}>
        <div style={{ display: 'flex', gap: '10px 22px', flexWrap: 'wrap' }}>
          {kpis.map(([n, label, color]) => (
            <div key={label} style={{ fontSize: '0.8rem', color: 'var(--body-ink)' }}>
              <b style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: color || 'var(--ink)' }}>{n}</b>
              {label}
            </div>
          ))}
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5, margin: '12px 0 0' }}>
          The CRM anchors on projects, not customers: one customer can carry several projects over the
          years, and a project can involve many parties — sponsors, partners, participants.
          {view === 'board' && ' Click a card to open the project.'}
        </p>
      </SectionCard>

      {view === 'board' ? board : list}

      <SectionCard title="Add project">
        <form onSubmit={add} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Input placeholder="Project" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ flex: 2, minWidth: 160 }} />
          <CompanyField
            value={form.customer}
            onChange={(customer, companyId) => setForm(f => ({ ...f, customer, companyId: companyId ?? null }))}
          />
          <Input placeholder="Contact" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} style={{ flex: 1, minWidth: 110 }} />
          <Select
            value={form.sourceType}
            onChange={e => setForm(f => ({ ...f, sourceType: e.target.value }))}
            title="How this prospect was sourced (feeds the Ice Pilot IDR: FAR-sourced share)"
            style={{ flex: 1, minWidth: 140, width: 'auto' }}
          >
            <option value="">Source…</option>
            <option value="FAR">FAR (recommendation)</option>
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
            <option value="referral">Referral</option>
          </Select>
          <Btn type="submit">Add project</Btn>
        </form>
        <p style={{ marginTop: 12, fontSize: '0.78rem', color: 'var(--ink-faint)', lineHeight: 1.5 }}>
          When a prospect becomes a known client with a concrete need, work continues in Master.
          On closing, graduation to the shared HB Clients registry is proposed automatically — and enters
          the registry only when approved by the Ice Pilot running that client.
        </p>
      </SectionCard>
      <CriteriaEditor onSaved={load} />
      <ImportExport onImported={load} />

      {open && <ProjectModal project={open} onClose={() => setOpenId(null)} onChanged={load} />}
      {openCompanyId && <CompanyModal companyId={openCompanyId} onClose={() => setOpenCompanyId(null)} />}
    </>
  );
}

// Company typeahead — dedupe at source: search existing companies + HB Clients
// registry as you type; pick one, or type a new name (the backend creates it).
function CompanyField({ value, onChange }) {
  const [results, setResults] = useState({ companies: [], registry: [] });
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [engagement, setEngagement] = useState(null);

  // Model UC1 — "who we know": when an existing company or registry client is picked,
  // show what the network already knows and whom to ask (+ the WI-B5 collision heads-up).
  function selectCompany(name, id, clientId = null) {
    onChange(name, id); setOpen(false); setSelectedId(id); setEngagement(null);
    if (id) api.explore.getCompanyEngagement(id).then(setEngagement).catch(() => {});
    else if (clientId) api.explore.getClientKnown(clientId).then(setEngagement).catch(() => {});
  }
  async function notify() {
    try { const r = await api.explore.notifyCompany(selectedId); alert(`Notified ${r.notified} colleague(s).`); }
    catch (e) { alert(e.message); }
  }

  useEffect(() => {
    let alive = true;
    if (!value || !value.trim()) { setResults({ companies: [], registry: [] }); return; }
    const t = setTimeout(() => {
      api.explore.getCompanies(value.trim())
        .then(d => { if (alive) setResults({ companies: d.companies || [], registry: d.registry || [] }); })
        .catch(() => {});
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [value]);

  const exact = results.companies.some(c => c.name.toLowerCase() === value.trim().toLowerCase());
  const show = open && value.trim() && (results.companies.length || results.registry.length || !exact);

  return (
    <div style={{ position: 'relative', flex: 2, minWidth: 160 }}>
      <Input
        placeholder="Customer / company"
        value={value}
        onChange={e => { onChange(e.target.value, null); setSelectedId(null); setEngagement(null); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {show && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'var(--canvas-white)', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', marginTop: 4, maxHeight: 240, overflowY: 'auto' }}>
          {results.companies.map(c => (
            <button key={`co${c.id}`} type="button" onMouseDown={() => selectCompany(c.name, c.id)}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer', fontSize: '0.86rem', fontFamily: 'var(--font-body)' }}>
              {c.name}
              <span style={{ color: 'var(--ink-faint)', fontSize: '0.76rem' }}>
                {' '}· {c.projectCount} project{c.projectCount === 1 ? '' : 's'}{c.inRegistry ? ' · in registry' : ''}
              </span>
            </button>
          ))}
          {results.registry.map(c => (
            <button key={`cl${c.clientId}`} type="button" onMouseDown={() => selectCompany(c.name, null, c.clientId)}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer', fontSize: '0.86rem', fontFamily: 'var(--font-body)' }}>
              {c.name}
              <span style={{ color: 'var(--ink-faint)', fontSize: '0.76rem' }}> · HB Clients registry</span>
            </button>
          ))}
          {!exact && value.trim() && (
            <div style={{ padding: '9px 12px', fontSize: '0.8rem', color: 'var(--ink-faint)', borderTop: '1px solid var(--line)' }}>
              New company: <b style={{ color: 'var(--ink)' }}>{value.trim()}</b>
            </div>
          )}
        </div>
      )}
      {engagement && <KnownByNetwork k={engagement} onNotify={selectedId ? notify : null} />}
    </div>
  );
}

// "The network already knows them" — registry facts, colleagues' won work, whom to ask.
function KnownByNetwork({ k, onNotify }) {
  const navigate = useNavigate();
  const r = k.registry;
  const ask = [];
  if (r?.hbContact) ask.push({ ...r.hbContact, why: 'HB contact' });
  (k.pastWork || []).forEach(w => { if (!ask.some(a => a.id === w.by.id)) ask.push({ ...w.by, why: `delivered “${w.project}”${w.year ? ` (${w.year})` : ''}` }); });
  if (!r && !(k.pastWork || []).length && !k.engagedBy.length) return null;
  const facts = r ? [
    r.trainings && `trained: ${r.trainings}`,
    r.countriesTrained && `in ${r.countriesTrained}`,
    r.lastEngagement && `last engagement ${r.lastEngagement}`,
    r.far && `FAR: ${r.far}`,
  ].filter(Boolean) : [];
  return (
    <div style={{ marginTop: 6, width: 'min(560px, 85vw)', position: 'relative', zIndex: 1, fontSize: '0.8rem', background: 'var(--canvas-warm)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 'var(--radius-sm)', padding: '9px 11px', lineHeight: 1.5 }}>
      {(r || (k.pastWork || []).length > 0) && (
        <div><b>Hansen Beck already knows this customer.</b>{r && !r.active ? ' (not an active client now)' : ''}{facts.length ? ` ${facts.join(' · ')}.` : ''}</div>
      )}
      {ask.length > 0 && (
        <div style={{ marginTop: 4 }}>Ask first:{' '}
          {ask.map((a, i) => (
            <span key={a.id}>{i > 0 && ', '}
              <button type="button" onMouseDown={() => navigate(`/community/chat?with=${a.id}`)} style={{ ...linkBtn, color: 'var(--ink)' }}>{a.name}</button>
              <span style={{ color: 'var(--ink-faint)' }}> — {a.why}</span>
            </span>
          ))}
        </div>
      )}
      {k.engagedBy.length > 0 && (
        <div style={{ marginTop: 4, color: 'var(--warn, #8a6d00)' }}>
          Already in conversation with them: {k.engagedBy.map(e => e.name).join(', ')}.{' '}
          {onNotify && <button type="button" onMouseDown={onNotify} style={{ ...linkBtn, color: 'inherit' }}>Notify them</button>}
        </div>
      )}
    </div>
  );
}

// Customer view — one company, all its projects across the years (WI-B4).
function CompanyModal({ companyId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.explore.getCompany(companyId).then(setData).catch(e => setError(e.message));
  }, [companyId]);
  const c = data?.company;
  return (
    <Modal open onClose={onClose} title={c ? c.name : 'Company'}>
      {error && <Alert type="error">{error}</Alert>}
      {!data ? <Loading /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)' }}>
            {[c.industry, c.country, c.inRegistry ? 'In HB Clients registry' : null].filter(Boolean).join(' · ') || 'No company details yet.'}
            {c.inRegistry && (c.trainings != null) && <> · {c.trainings} trainings delivered</>}
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 8 }}>
              Projects ({data.projects.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.projects.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: '0.86rem', paddingBottom: 6, borderBottom: '1px solid var(--line)' }}>
                  <span><b>{p.name}</b>{p.owner ? <span style={{ color: 'var(--ink-faint)' }}> · {p.owner}</span> : ''}</span>
                  <span style={{ color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>
                    {p.stage}{p.graduationStatus === 'approved' ? ' · in registry' : ''}
                  </span>
                </div>
              ))}
              {data.projects.length === 0 && <span style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>No projects visible to you.</span>}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── HB Clients registar ───────────────────────────────────────────────────
function Clients() {
  const [state, setState] = useState({ loading: true, error: null, clients: [] });
  const [q, setQ] = useState('');
  useEffect(() => {
    api.explore.getClients()
      .then(d => setState({ loading: false, error: null, clients: d.clients }))
      .catch(e => setState({ loading: false, error: e.message, clients: [] }));
  }, []);

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  const clients = state.clients.filter(c =>
    `${c.name} ${c.industry || ''} ${c.trainings || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <ClientsRegistry />
      <SectionCard title="HB Clients">
        <Input placeholder="Search clients…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 420 }} />
        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5, margin: '12px 0 0' }}>
          One shared intelligence base for the whole HB community — who trained where, which programmes,
          who to ask. The backbone of prospecting step 3, Check Existing Customers.
        </p>
      </SectionCard>
      <Card style={{ padding: '10px 24px 6px' }}>
        <Table
          headers={['Client', 'Industry', 'Geography', 'FAR?', 'Trainings delivered', 'Active', 'Last engagement', 'HB contact']}
          rows={clients.map(c => [
            <b>{c.name}</b>, c.industry || '—', c.geography || '—', c.far || '—',
            c.trainings || '—', c.active ? 'Yes' : 'No', c.lastEngagement || '—', c.hbContact || '—',
          ])}
          emptyMessage="No clients in the registry yet"
        />
      </Card>
    </>
  );
}

// ── My week (WI-B7 reminders) ──────────────────────────────────────────────
function MyWeek() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    api.explore.getMyWeek()
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);
  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  const { overdue, thisWeek } = state.data;

  const Section = ({ title, items, danger }) => (
    <SectionCard title={<span style={{ color: danger && items.length ? 'var(--danger)' : 'var(--ink)' }}>{title} ({items.length})</span>}>
      {items.length === 0 ? <p style={{ fontSize: '0.85rem', color: 'var(--ink-faint)', margin: 0 }}>Nothing here.</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(a => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: '0.86rem', borderBottom: '1px solid var(--line)', paddingBottom: 6 }}>
              <span>{a.title} <span style={{ color: 'var(--ink-faint)' }}>· {a.projectName} ({a.customer})</span></span>
              <span style={{ color: danger ? 'var(--danger)' : 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{String(a.dueDate).slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );

  return (
    <>
      <SectionCard title="My week">
        <p style={{ ...{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5, margin: '12px 0 0' }, margin: 0 }}>
          Your due and overdue next steps across every project. Add activities with dates from a project’s card.
          {' '}A daily e-mail digest of these items is planned (per-user opt-out).
        </p>
      </SectionCard>
      <div>
        <Section title="Overdue" items={overdue} danger />
        <Section title="Due this week" items={thisWeek} />
      </div>
    </>
  );
}

// ── CSV import / export (WI-B7) ────────────────────────────────────────────
function ImportExport({ onImported }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  function parse(csv) {
    const lines = csv.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const split = (l) => l.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    let start = 0;
    const first = split(lines[0]).map(s => s.toLowerCase());
    const looksHeader = first.some(h => ['project', 'name', 'company', 'customer', 'stage'].includes(h));
    if (looksHeader) start = 1;
    return lines.slice(start).map(l => {
      const c = split(l);
      return { name: c[0] || '', company: c[1] || '', stage: c[2] || '', value: c[3] || '', nextStep: c[4] || '' };
    });
  }

  async function doPreview() {
    const rows = parse(text);
    if (!rows.length) { alert('Paste CSV rows first (Project, Company, Stage, Value, NextStep).'); return; }
    setBusy(true);
    try { setPreview(await api.explore.importPreview(rows)); } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function commit() {
    setBusy(true);
    try {
      const rows = preview.rows.filter(r => r.valid && !r.duplicate);
      const r = await api.explore.importCommit(rows);
      alert(`Imported ${r.created}, skipped ${r.skipped}.`);
      setText(''); setPreview(null); setOpen(false); onImported();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function exportCsv() {
    try {
      const { csv } = await api.explore.exportCsv();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'hb-pipeline.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  }

  return (
    <SectionCard title="Import / export pipeline" style={{ marginTop: 16, marginBottom: 0 }} action={<>
      <Btn size="sm" variant="ghost" onClick={exportCsv}>Export CSV</Btn>
      <Btn size="sm" variant="outline" onClick={() => setOpen(o => !o)}>{open ? 'Close' : 'Import CSV'}</Btn>
    </>}>
      {open && (
        <div>
          <p style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 8 }}>
            Paste rows: <code>Project, Company, Stage, Value, NextStep</code> (a header row is fine). Preview shows dedupe before commit.
          </p>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
            placeholder="Retail Academy, Mercato Group, Needs Assessment, 24000, Intake call"
            style={{ width: '100%', padding: 10, borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', fontFamily: 'var(--font-body)', fontSize: '0.85rem' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn size="sm" variant="outline" loading={busy} onClick={doPreview}>Preview</Btn>
            {preview && <Btn size="sm" loading={busy} onClick={commit} disabled={!preview.willImport}>Import {preview.willImport} row(s)</Btn>}
          </div>
          {preview && (
            <div style={{ marginTop: 12, maxHeight: 260, overflowY: 'auto' }}>
              <Table
                headers={['Project', 'Company', 'Stage', 'Status']}
                rows={preview.rows.map(r => [
                  r.name || <span style={{ color: 'var(--danger)' }}>—</span>,
                  r.company,
                  r.stageName || r.stage || '—',
                  !r.valid ? <span style={{ color: 'var(--danger)' }}>{r.note}</span>
                    : r.duplicate ? <span style={{ color: 'var(--warn, #8a6d00)' }}>duplicate — skip</span>
                      : <span>{r.willCreateCompany ? 'new company' : 'existing company'}</span>,
                ])}
              />
              <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 6 }}>
                {preview.willImport} to import · {preview.duplicates} duplicate(s) skipped.
              </p>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// ── Funnel dashboard (WI-B3) ───────────────────────────────────────────────
const ANCHOR_LABEL = { E: 'Explore', M: 'Master', D: 'Deliver' };

// Day price is a territory setting (HB Way of Selling v1.1): new projects start from it, each project can
// change its own. The Business Partner sets it for the territory; Administration for any (or the network '*').
function TerritoryDayPrice() {
  const [t, setT] = useState(null);
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.explore.getTerritorySettings()
      .then(d => { setT(d); setPrice(d.dayPriceEur ?? ''); })
      .catch(() => setT(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!t) return null;
  async function save() {
    setBusy(true);
    try { await api.explore.setTerritorySettings({ territory: t.territory || '*', dayPriceEur: price === '' ? null : Number(price) }); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <Card style={{ padding: '22px 28px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', alignContent: 'flex-start' }}>
      <div style={{ flex: '1 1 100%' }}>
        <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', margin: '0 0 8px' }}>Training day price{t.territory ? ` — ${t.territory}` : ''}</h3>
        <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginTop: 4, lineHeight: 1.5 }}>
          {t.dayPriceEur != null ? `${Number(t.dayPriceEur).toLocaleString('en-GB')} EUR${t.ownSetting ? '' : ' (network default)'}` : 'Not set'} — new projects start from it; each project can change its own.
        </div>
      </div>
      {t.canEdit && (
        <>
          <Input type="number" min="0" value={price} onChange={e => setPrice(e.target.value)} style={{ maxWidth: 120 }} />
          <Btn size="sm" variant="outline" loading={busy} onClick={save}>Save for {t.territory || 'the network'}</Btn>
        </>
      )}
    </Card>
  );
}

function Funnel() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    api.explore.getFunnel()
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  const d = state.data;
  const maxCount = Math.max(1, ...d.stages.map(s => s.count));

  const kpis = [
    [d.kpis.companiesProspected, 'companies prospected'],
    [d.kpis.trainingDays, 'training days delivered'],
    [d.kpis.nextStepsOpen, 'next steps open'],
    [d.kpis.wonCount, 'closed won'],
  ];

  const cur = d.pipeline?.currency;
  return (
    <>
      {/* Key numbers first — pipeline value (WI-B6), then the KPIs */}
      <SectionCard title="Funnel">
        <div style={{ display: 'flex', gap: '14px 28px', flexWrap: 'wrap' }}>
          {d.pipeline && <Stat quiet value={`${d.pipeline.value.toLocaleString()} ${cur}`} label="open pipeline value" />}
          {d.pipeline && <Stat quiet value={`${d.pipeline.weighted.toLocaleString()} ${cur}`} label="weighted (× probability)" />}
          {kpis.map(([n, label]) => <Stat quiet key={label} value={n} label={label} />)}
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5, margin: '12px 0 0' }}>
          {d.scope === 'aggregate'
            ? 'Aggregate view — all projects across the network.'
            : 'Your funnel — only your own projects. The same figures feed your Grow “My Business Growth”.'}
        </p>
      </SectionCard>

      {/* Forecast + HB Compass adoption (W6) — secondary figures, side by side */}
      {((d.pipeline && d.forecast && d.forecast.length > 0) || (d.compass && (d.compass.withCompass + d.compass.withoutCompass > 0))) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
          {d.pipeline && d.forecast && d.forecast.length > 0 && (
            <Card style={{ padding: '16px 20px' }}>
              <SectionLabel>Weighted forecast</SectionLabel>
              {d.forecast.slice(0, 4).map(f => (
                <div key={f.period} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '3px 0' }}>
                  <span style={{ color: 'var(--ink-soft)' }}>{f.period}</span><b>{f.weighted.toLocaleString()} {cur}</b>
                </div>
              ))}
            </Card>
          )}
          {d.compass && (d.compass.withCompass + d.compass.withoutCompass > 0) && (
            <Card style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                <Stat quiet value={d.compass.withCompass} label="with HB Compass" />
                <Stat quiet value={d.compass.withoutCompass} label="without (direct)" />
              </div>
              <div style={{ fontSize: '0.76rem', color: 'var(--ink-faint)', marginTop: 10 }}>
                Compass adoption across role tracks that reached Gate 2.
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Stage funnel */}
      <SectionCard
        title="Pipeline by stage"
        hint={`${d.totals.active} active · ${d.totals.won} won · ${d.totals.lost} lost${d.totals.parked ? ` · ${d.totals.parked} parked / unresponsive` : ''}`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {d.stages.map(s => (
            <div key={s.seq} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 210, flexShrink: 0, fontSize: '0.85rem' }}>
                {s.anchor && <span style={{ color: 'var(--ink-faint)', fontSize: '0.72rem', marginRight: 5 }}>{ANCHOR_LABEL[s.anchor] || s.anchor}</span>}
                {s.seq}. {s.name}
              </div>
              <div style={{ flex: 1, minWidth: 0, background: 'var(--canvas-warm)', borderRadius: 6 }}>
                <div style={{
                  height: 26, borderRadius: 6, background: 'var(--ink)',
                  width: `${Math.max(s.count ? 8 : 0, (s.count / maxCount) * 100)}%`,
                  minWidth: s.count ? 26 : 0, display: 'flex', alignItems: 'center', paddingLeft: s.count ? 8 : 0,
                  color: '#fff', fontSize: '0.78rem', fontWeight: 600, transition: 'width var(--transition)',
                }}>
                  {s.count > 0 && s.count}
                </div>
              </div>
              <div style={{ width: 110, flexShrink: 0, fontSize: '0.74rem', color: 'var(--ink-faint)', textAlign: 'right' }}>
                {s.count ? `avg ${s.avgDays}d · max ${s.maxDays}d` : '—'}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Lost reasons + territory day price, side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, alignItems: 'stretch' }}>
        <SectionCard title="Lost reasons" style={{ marginBottom: 0 }}>
          {d.lost.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--ink-faint)', margin: 0 }}>No lost projects.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {d.lost.map(l => (
                <div key={l.reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                  <span>{l.reason}</span>
                  <b>{l.count}</b>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        <TerritoryDayPrice />
      </div>

      <p style={{ marginTop: 14, fontSize: '0.78rem', color: 'var(--ink-faint)' }}>
        Weighted pipeline uses each project’s value × probability. Add value &amp; a close date on a project to include it.
      </p>
    </>
  );
}

// ── My Customers — the single workplace (was: CRM) ────────────────────────
const TITLES = {
  journeys: 'Every journey in one place',
  funnel: 'My funnel — pipeline at a glance',
  week: 'My week — due & overdue next steps',
  clients: 'HB Clients — shared registry',
  find: 'Find clients — who is worth talking to, and about what',
};

export default function CustomersPages() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/customers/journeys" replace />;

  // A customer id (not a reserved tab) → the customer workspace (W2/W3).
  if (!TABS.includes(sub)) {
    if (!/^\d+$/.test(sub)) return <Navigate to="/customers/journeys" replace />;
    return (
      <BasecampLayout>
        <CustomerWorkspace id={sub} />
      </BasecampLayout>
    );
  }

  let body;
  if (sub === 'journeys') body = <Projects />;
  else if (sub === 'funnel') body = <Funnel />;
  else if (sub === 'week') body = <MyWeek />;
  else if (sub === 'clients') body = <Clients />;
  else if (sub === 'find') body = <FindClients />;

  return (
    <BasecampLayout>
      <PageHeader
        title="My Pipeline"
        subtitle={TITLES[sub] || 'every journey, one place'}
      />
      <AreaLayout items={CUSTOMERS_SUBNAV}>{body}</AreaLayout>
    </BasecampLayout>
  );
}